// ReflectionEditor.tsx — open a saved reflection: the prompt sits above, the
// answer is editable, and it can be deleted. Used from the Reflections tab.
import React from 'react';
import { Meta, Pressable, Card, Btn } from '@/components/ui';
import { Icon } from '@/components/icons';
import { useProgress } from '@/hooks/useProgress';
import { storage } from '@/services/storage';
import type { SavedReflection } from '@/services/progress';

// An in-progress edit is debounce-persisted under this key (per reflection), so
// a force-quit / backgrounded / accidental close never loses unsaved work. The
// draft is cleared once the edit is safely Saved or Deleted.
const draftKey = (stepId: string) => `renurture.reflection.draft.${stepId}`;

export function ReflectionEditor({
  reflection,
  onClose,
}: {
  reflection: SavedReflection;
  onClose: () => void;
}) {
  const progress = useProgress();
  // Prefer a recovered draft over the saved body — it's the most recent unsaved
  // work. Only restore when it actually differs from what was last saved.
  const [note, setNote] = React.useState(() => {
    const draft = storage.get(draftKey(reflection.stepId));
    return draft != null && draft !== reflection.body ? draft : reflection.body;
  });
  const [saved, setSaved] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [confirmClose, setConfirmClose] = React.useState(false);

  // Debounced autosave of the in-progress text. Clears the draft when the text
  // matches the saved body again (nothing unsaved to recover).
  React.useEffect(() => {
    const t = window.setTimeout(() => {
      if (note === reflection.body) storage.remove(draftKey(reflection.stepId));
      else storage.set(draftKey(reflection.stepId), note);
    }, 800);
    return () => window.clearTimeout(t);
  }, [note, reflection.body, reflection.stepId]);

  const save = () => {
    const body = note.trim();
    storage.remove(draftKey(reflection.stepId));
    if (!body) {
      progress.deleteReflection(reflection.stepId);
      onClose();
      return;
    }
    progress.saveReflection({
      stepId: reflection.stepId,
      module: reflection.module,
      prompt: reflection.prompt,
      body,
    });
    setSaved(true);
    window.setTimeout(onClose, 600);
  };

  const remove = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      window.setTimeout(() => setConfirmDelete(false), 4000);
      return;
    }
    storage.remove(draftKey(reflection.stepId));
    progress.deleteReflection(reflection.stepId);
    onClose();
  };

  // Guard the close: if there are unsaved edits, ask once before discarding.
  const close = () => {
    const dirty = note.trim() !== reflection.body.trim() && note.trim().length > 0;
    if (dirty && !confirmClose) {
      setConfirmClose(true);
      window.setTimeout(() => setConfirmClose(false), 4000);
      return;
    }
    onClose();
  };

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 82,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 260,
            background:
              'radial-gradient(120% 80% at 50% -10%, rgba(255,250,240,0.95), rgba(246,224,190,0.16) 44%, transparent 64%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            padding: 'calc(18px + var(--safe-top)) 18px 0',
            position: 'relative',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Pressable
            as="button"
            onClick={close}
            ariaLabel={confirmClose ? 'Discard unsaved changes' : 'Close'}
            style={
              confirmClose
                ? {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    minHeight: 44,
                    padding: '0 14px',
                    borderRadius: 999,
                    border: '1px solid var(--hair)',
                    background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
                    color: '#b23b2e',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: 13.5,
                    fontWeight: 600,
                    boxShadow: 'var(--edge), var(--sh1)',
                  }
                : {
                    width: 44,
                    height: 44,
                    borderRadius: 999,
                    border: '1px solid var(--hair)',
                    background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
                    color: 'var(--ink)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: 'var(--edge), var(--sh1)',
                  }
            }
          >
            <Icon name="chevDown" size={20} /> {confirmClose ? 'Discard changes?' : ''}
          </Pressable>
          <Pressable
            as="button"
            onClick={remove}
            ariaLabel="Delete reflection"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              minHeight: 44,
              padding: '0 12px',
              borderRadius: 999,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: confirmDelete ? '#b23b2e' : 'var(--ink-soft)',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 13.5,
              fontWeight: 600,
            }}
          >
            <Icon name="leaf" size={16} /> {confirmDelete ? 'Tap to confirm' : 'Delete'}
          </Pressable>
        </div>

        <div style={{ padding: '16px 26px 30px', position: 'relative' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              color: 'var(--green-deep)',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.04em',
              marginBottom: 12,
            }}
          >
            <Icon name="reflections" size={16} /> {reflection.module || 'Reflection'}
          </div>

          {/* the prompt, above */}
          {reflection.prompt && (
            <div style={{ borderLeft: '2px solid var(--amber)', paddingLeft: 16, marginBottom: 20 }}>
              <p
                style={{
                  margin: 0,
                  fontFamily: 'Fraunces, Georgia, serif',
                  fontStyle: 'italic',
                  fontWeight: 300,
                  fontSize: 20,
                  lineHeight: 1.5,
                  color: 'var(--amber-deep)',
                }}
              >
                {reflection.prompt}
              </p>
            </div>
          )}

          <Card well pad={3} radius={18}>
            <textarea
              autoFocus
              value={note}
              onChange={(ev) => {
                setNote(ev.target.value);
                setSaved(false);
              }}
              aria-label="Your reflection"
              placeholder="Your words. Edit freely, or clear to delete."
              style={{
                width: '100%',
                minHeight: 200,
                resize: 'none',
                boxSizing: 'border-box',
                background: 'transparent',
                border: 'none',
                borderRadius: 16,
                padding: 15,
                color: 'var(--ink)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 16,
                lineHeight: 1.6,
                outline: 'none',
              }}
            />
          </Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 12 }}>
            <span style={{ color: 'var(--green-deep)' }}>
              <Icon name="shield" size={15} />
            </span>
            <Meta>Private — kept on your device, never shared</Meta>
          </div>
        </div>
      </div>

      <div
        style={{
          padding: '14px 26px calc(22px + var(--safe-bottom))',
          borderTop: '1px solid var(--hair)',
          background: 'linear-gradient(180deg, transparent, var(--cream) 30%)',
        }}
      >
        <Btn full size="lg" onClick={save} ariaLabel="Save reflection" leading={saved ? <Icon name="check" size={18} sw={2} /> : undefined}>
          {saved ? 'Saved' : 'Save changes'}
        </Btn>
      </div>
    </div>
  );
}
